import { CTA, Header, Page } from "components/shared"
import { FAQs, MoreInfo, Notes, Overview, Pricing, Subnav } from "components/treatments"
import { kebabCase } from "lodash"

import { IMAGE, SEO } from "utils/fragments"
import { flattenData, useQuery, gql } from "/utils/api"

const SECTIONS = {
  ComponentTreatmentOverview: {
    nav: "Overview",
    component: Overview,
  },
  ComponentTreatmentMoreInfo: {
    nav: "Treatment",
    component: MoreInfo,
  },
  // ComponentTreatmentResults: {
  //   nav: "Stories",
  //   component: BeforeAndAfter,
  // },
  ComponentTreatmentNotes: {
    nav: "Notes",
    component: Notes,
  },
  ComponentTreatmentPricing: {
    nav: "Pricing",
    component: Pricing,
  },

  ComponentTreatmentFaQs: {
    nav: "FAQs",
    component: FAQs,
  },

  ComponentTreatmentCallToAction: {
    component: CTA,
  },
}

const AestheticTreatment = ({ treatment: { seo, title, description, image, sections } }) => {
  return (
    <Page seo={seo}>
      <Header
        layout="treatment"
        title={title}
        description={description}
        image={image}
        cta={{
          button: { label: "Book an Appointment", link: "/consultation?treatment=botox" },
          buttonTwo: { label: "Buy a Gift Voucher", link: "https://app.cardivo.com/giftcards/tirnanog", isExternal: true },
        }}
      />
      <Subnav
        items={Object.keys(SECTIONS).map((sectionKey) => {
          const hasSection = sections.find((s) => s.__typename === sectionKey)
          return hasSection && SECTIONS[sectionKey]?.nav
        })}
      />
      <div>
        {sections.map((section, index) => {
          const id = SECTIONS[section.__typename]?.nav
          const Component = SECTIONS[section.__typename]?.component
          let componentProps = { ...section }
          if (section.__typename === "ComponentTreatmentCallToAction") {
            componentProps = {
              ...componentProps,
              button: { label: "Book an Appointment", link: "/consultation?treatment=botox" },
            }
          }

          return Component && <Component key={index} id={kebabCase(id)} {...componentProps} />
        })}
      </div>
    </Page>
  )
}

export async function getStaticPaths() {
  const query = gql`
    query AestheticTreatments {
      aestheticTreatments {
        data {
          attributes {
            slug
          }
        }
      }
    }
  `

  const { data } = await useQuery(query)

  return {
    paths: data.aestheticTreatments.data.map((treatment) => ({
      params: {
        slug: treatment.attributes.slug,
      },
    })),
    fallback: "blocking",
  }
}

export async function getStaticProps({ params }) {
  const query = gql`
    ${IMAGE}
    ${SEO}
    query AestheticTreatments($slug: String) {
      treatment: findSlug(modelName: "aesthetic-treatment", slug: $slug) {
        ... on AestheticTreatmentEntityResponse {
          data {
            attributes {
              seo {
                ...seo
              }
              title
              description
              slug
              image {
                ...Image
              }
              sections: Sections {
                ... on ComponentTreatmentOverview {
                  overview
                  benefits {
                    benefit
                  }
                  whoDescription
                  candidates {
                    candidate
                  }
                }
                ... on ComponentTreatmentProcess {
                  description
                  image {
                    ...Image
                  }
                  process {
                    title
                    description
                  }
                }
                ... on ComponentTreatmentMoreInfo {
                  title
                  subtitle
                  description
                  image {
                    ...Image
                  }
                }
                ... on ComponentTreatmentResults {
                  results {
                    description
                    duration
                    cost
                    grafts
                    treatment
                    before {
                      ...Image
                    }
                    after {
                      ...Image
                    }
                  }
                }
                ... on ComponentTreatmentPricing {
                  description
                }
                ... on ComponentTreatmentFaQs {
                  questions {
                    question
                    answer
                  }
                }
                ... on ComponentTreatmentNotes {
                  notes {
                    title
                    description
                  }
                }
                ... on ComponentTreatmentCallToAction {
                  title
                  description
                }
              }
            }
          }
        }
      }
    }
  `

  const { data } = await useQuery(query, { slug: params.slug })
  const treatmentData = addCTAButton(data)
  // const treatmentData = data

  return {
    props: { ...treatmentData },
    revalidate: 60,
  }
}

export default AestheticTreatment

const addCTAButton = (data) => {
  const flattenedData = flattenData(data)
  const CTASection = flattenedData?.treatment?.sections?.find((section) => section?.__typename === "ComponentTreatmentCallToAction")
  let newSections = flattenedData?.treatment?.sections?.filter((section) => section?.__typename !== "ComponentTreatmentCallToAction")
  const newCTASection = {
    ...CTASection,
    button: {
      label: "Request a Consultation",
      href: "/contact",
    },
  }

  newSections = [...newSections, newCTASection]

  return {
    ...flattenedData,
    treatment: {
      ...flattenedData.treatment,
      sections: [...newSections],
    },
  }
}
